import { useEffect, useId, useRef, useState } from 'react';
import { authConfig } from '../../config/auth-config.ts';
import { signedFetch } from '../../auth/signedFetch.ts';
import './EmailInput.css';

interface Props {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly label: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  /**
   * Fires whenever the overall validity (both criteria met) changes.
   * SignUp uses this to gate its submit button.
   */
  readonly onValidityChange?: (valid: boolean) => void;
}

// Loose email shape — Cognito does its own server-side validation; this
// is the UX gate. Semantically matches the email-check Lambda's
// defence-in-depth check, with bounded quantifiers (ReDoS hygiene);
// check (lib/lambda/email-check/index.ts).
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,24}$/;

type AsyncState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'error'; message: string };

// Wait this long after the last keystroke before hitting the
// availability-check Lambda, so typing an email doesn't fire a request
// per character. Exported so tests advance their fake clock by the same
// amount instead of hard-coding the value.
export const DEBOUNCE_MS = 3000;

export default function EmailInput({
  value,
  onChange,
  label,
  disabled,
  required,
  onValidityChange,
}: Props) {
  const id = useId();
  const helpId = useId();
  const [asyncState, setAsyncState] = useState<AsyncState>({ kind: 'idle' });

  const abortRef = useRef<AbortController | null>(null);
  const seqRef = useRef(0);

  const formatValid = EMAIL_RE.test(value) && value.length <= 254;
  const overallValid = formatValid && asyncState.kind === 'available';

  const lastReportedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastReportedRef.current !== overallValid) {
      lastReportedRef.current = overallValid;
      onValidityChange?.(overallValid);
    }
  }, [overallValid, onValidityChange]);

  useEffect(() => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (!formatValid) {
      setAsyncState({ kind: 'idle' });
      return;
    }
    setAsyncState({ kind: 'checking' });
    const mySeq = ++seqRef.current;
    const handle = window.setTimeout(() => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const url = `${authConfig.emailCheckUrl}?e=${encodeURIComponent(value)}`;
      signedFetch(url, { signal: ctrl.signal })
        .then(async (res) => {
          if (seqRef.current !== mySeq) {
            return;
          }
          if (!res.ok) {
            setAsyncState({ kind: 'error', message: `check failed (${res.status})` });
            return;
          }
          const json = (await res.json()) as { available?: boolean };
          if (seqRef.current !== mySeq) {
            return;
          }
          if (json.available === true) {
            setAsyncState({ kind: 'available' });
          } else if (json.available === false) {
            setAsyncState({ kind: 'taken' });
          } else {
            setAsyncState({ kind: 'error', message: 'unexpected response' });
          }
        })
        .catch((err: unknown) => {
          if (seqRef.current !== mySeq) {
            return;
          }
          if (err instanceof Error && err.name === 'AbortError') {
            return;
          }
          const message = err instanceof Error ? err.message : 'check failed';
          setAsyncState({ kind: 'error', message });
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [formatValid, value]);

  const uniqueLabel =
    asyncState.kind === 'taken'
      ? 'Email is already in use'
      : asyncState.kind === 'error'
        ? `Availability check unavailable (${asyncState.message})`
        : 'Email is available';
  const uniqueMet = asyncState.kind === 'available';
  const uniqueChecking = asyncState.kind === 'checking';
  const uniqueErrored = asyncState.kind === 'error';
  const uniqueRowState: 'met' | 'unmet' | 'checking' | 'error' = uniqueMet
    ? 'met'
    : uniqueChecking
      ? 'checking'
      : uniqueErrored
        ? 'error'
        : 'unmet';
  const uniqueIcon =
    uniqueRowState === 'met'
      ? '✓'
      : uniqueRowState === 'checking'
        ? '⋯'
        : '✕';

  return (
    <div className="om-email">
      <label htmlFor={id} className="om-email__label">
        {label}
      </label>
      <input
        id={id}
        type="email"
        className="om-email__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="email"
        required={required}
        disabled={disabled}
        spellCheck={false}
        aria-describedby={helpId}
      />
      <ul
        id={helpId}
        className="om-email__criteria"
        aria-label="Email requirements"
        aria-live="polite"
      >
        <li
          className="om-email__criterion"
          data-state={formatValid ? 'met' : 'unmet'}
          aria-label={`Valid email format: ${formatValid ? 'met' : 'not met'}`}
        >
          <span className="om-email__criterion-icon" aria-hidden="true">
            {formatValid ? '✓' : '✕'}
          </span>
          <span className="om-email__criterion-label">Valid email format</span>
        </li>
        <li
          className="om-email__criterion"
          data-state={uniqueRowState}
          aria-label={`${uniqueLabel}: ${
            uniqueRowState === 'met'
              ? 'met'
              : uniqueRowState === 'checking'
                ? 'checking'
                : 'not met'
          }`}
        >
          <span className="om-email__criterion-icon" aria-hidden="true">
            {uniqueIcon}
          </span>
          <span className="om-email__criterion-label">{uniqueLabel}</span>
        </li>
      </ul>
    </div>
  );
}
